import * as React from "react";
import { useEffect, useState } from "react";
import { Flag16Regular, Clock16Regular } from "@fluentui/react-icons";
import { REFINED } from "../../risikoanalyseStep/tokens";
import { deDate as deIsoDate } from "../../dashboard/DashboardShared";
import { formatRelativeDay } from "../../../utils/relativeDay";

/**
 * Die Datums-Zellen der Vorgangstabelle — herausgelöst aus `VorgaengeMo.tsx`,
 * seit der Vorgänge-Reiter der Kontaktakte dieselben Spalten zeigt. Zwei
 * Ansichten desselben Termins auseinanderdriften zu lassen wäre der sichere
 * Weg zu „in der Tabelle steht was anderes als im Kontakt".
 */

/**
 * Datums-Zelle für Frist / TWV / Erstellungsdatum. Alle drei zeigen dieselbe
 * zweizeilige Struktur — Icon + relative Tagesangabe („heute", „morgen",
 * „Freitag", „nächste Woche Montag") mit dem konkreten Datum klein darunter,
 * damit ein Termin ohne Kopfrechnen einzuordnen ist. Die Gewichtung bleibt
 * unterschiedlich:
 *  - variant="frist" (Deadline, handlungsleitend): Flaggen-Icon, fett, überfällig rot.
 *  - variant="twv" (Wiedervorlage, Reminder): Uhr-Icon, dezent grau — der Frist
 *    visuell untergeordnet, aber genauso anschaulich.
 *  - variant="erstellt" (Vergangenheit): nur das Datum, ohne Icon; eine relative
 *    Angabe gibt es für vergangene Tage ohnehin nicht.
 */
export const DateCell: React.FC<{ iso?: string | null; overdue?: boolean; variant?: "frist" | "twv" | "erstellt" }> = ({
    iso,
    overdue = false,
    variant = "frist",
}) => {
    if (!iso) return null;
    const date = deIsoDate(iso);
    if (variant === "erstellt") {
        return (
            <span style={{ fontSize: REFINED.fs.sm, color: REFINED.ink2, whiteSpace: "nowrap" }} title={new Date(iso).toLocaleString("de-DE")}>
                {date}
            </span>
        );
    }
    const rel = formatRelativeDay(iso);
    const istTwv = variant === "twv";
    const farbe = overdue ? REFINED.err : istTwv ? REFINED.ink2 : REFINED.ink;
    return (
        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
            <span
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    fontWeight: istTwv ? 600 : 700,
                    fontSize: REFINED.fs.sm,
                    color: farbe,
                }}
            >
                {istTwv ? (
                    <Clock16Regular style={{ color: REFINED.ink3, flexShrink: 0 }} />
                ) : (
                    <Flag16Regular style={{ color: overdue ? REFINED.err : REFINED.aub, flexShrink: 0 }} />
                )}
                {rel ?? date}
            </span>
            {rel && <span style={{ fontSize: REFINED.fs.xs, color: REFINED.ink3, marginLeft: 21 }}>{date}</span>}
        </div>
    );
};

/**
 * Frist/TWV DIREKT in der Tabelle ändern (PO 03.08.2026: „dann sollte man auch
 * in der uebersicht vorgangstabelle also bei twv oder frist aendern koennen dort
 * direkt"). Ein Klick auf das Datum öffnet ein natives Datumsfeld in der Zelle;
 * Enter/Blur speichert, Escape bricht ab, „×" löscht den Termin.
 *
 * Ohne Schreibrecht (`editable=false`) bleibt es bei der reinen Anzeige — der
 * Klick fällt dann auf die Zeile durch und öffnet wie gewohnt den Vorgang.
 */
export const DateEditCell: React.FC<{
    iso?: string | null;
    overdue?: boolean;
    variant?: "frist" | "twv";
    editable: boolean;
    speichert: boolean;
    onSave: (wert: string | null) => void;
}> = ({ iso, overdue = false, variant = "frist", editable, speichert, onSave }) => {
    const [offen, setOffen] = useState(false);
    const [wert, setWert] = useState<string>(iso ? String(iso).slice(0, 10) : "");

    useEffect(() => {
        if (!offen) setWert(iso ? String(iso).slice(0, 10) : "");
    }, [iso, offen]);

    if (!editable) return <DateCell iso={iso} overdue={overdue} variant={variant} />;

    if (offen) {
        const uebernehmen = () => {
            setOffen(false);
            const neu = wert ? wert : null;
            const alt = iso ? String(iso).slice(0, 10) : null;
            if (neu !== alt) onSave(neu);
        };
        return (
            <span
                style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                onClick={(e) => e.stopPropagation()}
            >
                <input
                    type="date"
                    className="vg-datecell-input"
                    autoFocus
                    value={wert}
                    onChange={(e) => setWert(e.target.value)}
                    onBlur={uebernehmen}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            (e.target as HTMLInputElement).blur();
                        } else if (e.key === "Escape") {
                            e.preventDefault();
                            setWert(iso ? String(iso).slice(0, 10) : "");
                            setOffen(false);
                        }
                    }}
                />
            </span>
        );
    }

    return (
        <span
            role="button"
            tabIndex={0}
            title={variant === "twv" ? "Wiedervorlage ändern" : "Frist ändern"}
            className="vg-datecell-edit"
            style={{ opacity: speichert ? 0.5 : 1 }}
            onClick={(e) => {
                e.stopPropagation();
                setOffen(true);
            }}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    setOffen(true);
                }
            }}
        >
            {iso ? (
                <DateCell iso={iso} overdue={overdue} variant={variant} />
            ) : (
                <span style={{ color: REFINED.ink3, fontSize: REFINED.fs.sm }}>—</span>
            )}
        </span>
    );
};
